import dayjs from 'utils/dayjs';

import {
  FETCH_CURRENCIES_CROSS_RATES_REQUEST,
  FETCH_CURRENCIES_CROSS_RATES_SUCCESS,
  FETCH_CURRENCIES_CROSS_RATES_FAILURE,
  FETCH_CURRENCY_RATES_FROM_URL_REQUEST,
  FETCH_CURRENCY_RATES_FROM_URL_SUCCESS,
  FETCH_CURRENCY_RATES_FROM_URL_FAILURE,
  FETCH_MONTHLY_EXCHANGE_RATE_REQUEST,
  FETCH_MONTHLY_EXCHANGE_RATE_SUCCESS,
  FETCH_MONTHLY_EXCHANGE_RATE_FAILURE,
  FETCH_YEARLY_EXCHANGE_RATE_REQUEST,
  FETCH_YEARLY_EXCHANGE_RATE_SUCCESS,
  FETCH_YEARLY_EXCHANGE_RATE_FAILURE,
  FETCH_CURRENCIES_INFO_REQUEST,
  FETCH_CURRENCIES_INFO_SUCCESS,
  FETCH_CURRENCIES_INFO_FAILURE,
  FETCH_BANKS_REQUEST,
  FETCH_BANKS_SUCCESS,
  FETCH_BANKS_FAILURE,
  FETCH_CONVERTOR_RATES_GRAPH_FAILURE,
  FETCH_CONVERTOR_RATES_GRAPH_SUCCESS,
  FETCH_CONVERTOR_RATES_GRAPH_REQUEST,
  FETCH_CURRENCIES_REQUEST,
  FETCH_CURRENCIES_FAILURE,
  FETCH_CURRENCIES_SUCCESS,
  FETCH_LINKS_DATA_REQUEST,
  FETCH_LINKS_DATA_SUCCESS,
  FETCH_LINKS_DATA_FAILURE,
} from 'actionTypes';
import {
  getCurrenciesCrossRatesRequestKey,
  getCurrenciesCrossRatesIsFetching,
  getCurrenciesCrossRatesIsLoaded,
  getCurrenciesCrossRatesItems,
  makeGetLocationParamsValues,
  makeGetCurrencyRatesByParams,
  makeGetMonthlyExchangeRateDynamics,
  makeGetCurrenciesPairInfo,
  makeGetYearlyExchangeRateDynamics,
  getIsCurrenciesInfoInitialized,
  makeGetSortedBanksRates,
  getBanksIds,
  makeGetConvertorRatesGraphIsFetching,
  makeGetConvertorRatesGraphIsInitialized,
  getCurrenciesFetching,
  getCurrenciesItems,
  getIsLinksDataFetching,
  getIsLinksDataInitialized,
} from 'selectors';

import isEmpty from 'lodash.isempty';

import {
  request,
  getLocaleInDBFormat,
  getCurrencyTypeInURLFormat,
  DEFAULT_MOBILE_DATE_FORMAT,
  getURIByLocationWithParams,
  filterExcludedCurrencies,
  filterHard404Currencies,
  filterExcludedLinks,
} from 'utils';

import { normalize } from 'normalizr';

import {
  convertorCurrenciesInfo as currenciesInfoSchema,
  convertorCurrencies as currenciesSchema,
  convertorBanks as banksSchema,
  ratesGraph as schema,
  convertorLinks as linksSchema,
} from 'schemas';

import differenceBy from 'lodash.differenceby';

const getLocationParamsValues = makeGetLocationParamsValues();

const shouldFetchCurrenciesCrossRates = (state, props) => {
  const isFetching = getCurrenciesCrossRatesIsFetching(state, props);

  if (isFetching) {
    return false;
  }

  const isLoaded = getCurrenciesCrossRatesIsLoaded(state, props);
  const rates = getCurrenciesCrossRatesItems(state, props);
  const hasRates = Boolean(rates?.sell && Object.keys(rates.sell).length > 0);

  return !isLoaded || !hasRates;
};

export const fetchCurrenciesCrossRates = params => async (
  dispatch,
  getState
) => {
  const currentState = getState();

  if (!shouldFetchCurrenciesCrossRates(currentState, params)) {
    return true;
  }

  const { type } = params.match.params;
  const requestKey = getCurrenciesCrossRatesRequestKey(currentState, params);
  const requestUrl = `${process.env.APP_CURRENCIES_API_URL}/ratelist/?${requestKey}`;

  dispatch({
    type: FETCH_CURRENCIES_CROSS_RATES_REQUEST,
    payload: { requestKey },
  });

  try {
    const response = await request({
      url: requestUrl,
    });

    return dispatch({
      type: FETCH_CURRENCIES_CROSS_RATES_SUCCESS,
      payload: { ...(response || {}), type, requestKey },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_CURRENCIES_CROSS_RATES_FAILURE,
      payload: { requestKey },
    });
  }
};

const getCurrencyRatesByParams = makeGetCurrencyRatesByParams();

export const fetchCurrencyRatesFromUrl = params => async (
  dispatch,
  getState
) => {
  const currentState = getState();

  const {
    sourceCurrency,
    targetCurrency,
    sourceType,
    targetType,
    date,
  } = getLocationParamsValues(currentState, params);

  const { source, target } = getCurrencyRatesByParams(currentState, params);
  const locale = params.locale || params.intl.locale;

  if (source && target) {
    return true;
  }

  dispatch({
    type: FETCH_CURRENCY_RATES_FROM_URL_REQUEST,
    payload: {},
  });

  const requestUrl = `${
    process.env.APP_CURRENCIES_API_URL
  }/?locale=${getLocaleInDBFormat(
    locale
  )}&val=1&cur1=${sourceCurrency}&cur2=${targetCurrency}&cur1_type=${sourceType}&cur2_type=${targetType}&date=${date}`;

  try {
    const response = await request({
      url: requestUrl,
    });

    const rates =
      Array.isArray(response.data) || !response.data ? {} : response.data;

    const [sourceRates = {}, targetRates = {}] = Object.values(rates);

    return dispatch({
      type: FETCH_CURRENCY_RATES_FROM_URL_SUCCESS,
      payload: {
        rates: {
          ...(isEmpty(source)
            ? {
                [`${sourceCurrency}${getCurrencyTypeInURLFormat(
                  sourceType
                )}`]: sourceRates,
              }
            : {}),
          ...(isEmpty(target)
            ? {
                [`${targetCurrency}${getCurrencyTypeInURLFormat(
                  targetType
                )}`]: targetRates,
              }
            : {}),
        },
        date,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_CURRENCY_RATES_FROM_URL_FAILURE,
      payload: {},
    });
  }
};

const getMonthlyExchangeRateDynamics = makeGetMonthlyExchangeRateDynamics();
const getCurrenciesPairInfo = makeGetCurrenciesPairInfo();

export const fetchMonthlyExchangeRateDynamics = params => async (
  dispatch,
  getState
) => {
  const currentState = getState();

  const monthlyExchangeRateDynamics = getMonthlyExchangeRateDynamics(
    currentState,
    params
  );

  if (monthlyExchangeRateDynamics) {
    return true;
  }

  dispatch({
    type: FETCH_MONTHLY_EXCHANGE_RATE_REQUEST,
    payload: {},
  });

  const { source, target } = getCurrenciesPairInfo(currentState, params);

  const sourceSlug =
    params.match.params.sourceType === '-c' ? source?.title : source?.slug;
  const targetSlug =
    params.match.params.targetType === '-c' ? target?.title : target?.slug;

  // Перевіряємо чи є валідні slug перед запитом
  if (!sourceSlug || !targetSlug) {
    return dispatch({
      type: FETCH_MONTHLY_EXCHANGE_RATE_FAILURE,
      payload: {},
    });
  }

  const requestDate = dayjs()
    .subtract(30, 'days')
    .format(DEFAULT_MOBILE_DATE_FORMAT);

  try {
    const response = await request({
      url: `${process.env.APP_EXCHANGE_RATE_DYNAMICS_API}/day/${sourceSlug}/${targetSlug}/${requestDate}/`,
    });

    return dispatch({
      type: FETCH_MONTHLY_EXCHANGE_RATE_SUCCESS,
      payload: {
        rates: response.data,
        slugKey: `${source.slug}-${target.slug}`,
        typeKey: 'month',
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_MONTHLY_EXCHANGE_RATE_FAILURE,
      payload: {},
    });
  }
};

const getYearlyExchangeRateDynamics = makeGetYearlyExchangeRateDynamics();

export const fetchYearlyExchangeRateDynamics = params => async (
  dispatch,
  getState
) => {
  const currentState = getState();

  const yearlyExchangeRateDynamics = getYearlyExchangeRateDynamics(
    currentState,
    params
  );

  if (yearlyExchangeRateDynamics) {
    return true;
  }

  dispatch({
    type: FETCH_YEARLY_EXCHANGE_RATE_REQUEST,
    payload: {},
  });

  const { source, target } = getCurrenciesPairInfo(currentState, params);

  const sourceSlug =
    params.match.params.sourceType === '-c' ? source?.title : source?.slug;
  const targetSlug =
    params.match.params.targetType === '-c' ? target?.title : target?.slug;

  if (!sourceSlug || !targetSlug) {
    return dispatch({
      type: FETCH_YEARLY_EXCHANGE_RATE_FAILURE,
      payload: {},
    });
  }

  try {
    const response = await request({
      url: `${process.env.APP_EXCHANGE_RATE_DYNAMICS_API}/365/${sourceSlug}/${targetSlug}/`,
    });

    return dispatch({
      type: FETCH_YEARLY_EXCHANGE_RATE_SUCCESS,
      payload: {
        rates: response.data,
        slugKey: `${source.slug}-${target.slug}`,
        typeKey: 'year',
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_YEARLY_EXCHANGE_RATE_FAILURE,
      payload: {},
    });
  }
};

const getSlug = (condition, currency) =>
  condition ? '' : `&slug[]=${currency}`;

const getSlugs = currencies =>
  currencies.reduce(
    (result, currency) => `${result}${getSlug(false, currency)}`,
    ''
  );

export const fetchCurrenciesInfo = params => async (dispatch, getState) => {
  const currentState = getState();

  const {
    sourceCurrency,
    targetCurrency,
    currenciesList,
  } = getLocationParamsValues(currentState, params);

  const { source, target } = getCurrenciesPairInfo(currentState, params);
  const isInitialized = getIsCurrenciesInfoInitialized(currentState);

  const isSource = Object.keys(source).length > 0;
  const isTarget = Object.keys(target).length > 0;
  const locale = params.locale || params.intl.locale;

  if (isSource && isTarget) {
    return true;
  }

  dispatch({
    type: FETCH_CURRENCIES_INFO_REQUEST,
    payload: {},
  });

  const requestUrl = `${
    process.env.APP_CURRENCIES_API_URL
  }/list/?locale=${getLocaleInDBFormat(locale)}${getSlug(
    isSource,
    sourceCurrency
  )}${getSlug(isTarget, targetCurrency)}${getSlugs(currenciesList)}${
    isInitialized ? '' : '&group[]=priority&group[]=base'
  }`;

  try {
    const response = await request({
      url: requestUrl,
    });

    const filteredList = filterHard404Currencies(response.list || []);

    const {
      entities: { currenciesInfo: entities = {} } = {},
      result: ids = [],
    } = normalize(filteredList, currenciesInfoSchema);

    return dispatch({
      type: FETCH_CURRENCIES_INFO_SUCCESS,
      payload: { entities, ids },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_CURRENCIES_INFO_FAILURE,
      payload: {},
    });
  }
};

const getSortedBanksRates = makeGetSortedBanksRates();

const getQueryIds = rates =>
  rates.reduce((result, rate) => `${result}&id[]=${rate.companyId}`, '');

export const fetchBanks = params => async (dispatch, getState) => {
  const currentState = getState();
  const { sourceBanksRates: rates } = getSortedBanksRates(currentState, params);
  const banksIds = getBanksIds(currentState).map(id => ({
    companyId: id,
  }));
  const diffRates = differenceBy(rates, banksIds, 'companyId');
  const locale = params.locale || params.intl.locale;

  if (!diffRates.length) {
    return true;
  }
  dispatch({
    type: FETCH_BANKS_REQUEST,
    payload: {},
  });
  const requestUrl = `${
    process.env.APP_BANKS_API_URL
  }/?locale=${getLocaleInDBFormat(locale)}&minfin=1&show=all${getQueryIds(
    diffRates
  )}`;

  try {
    const response = await request({
      url: requestUrl,
    });

    const {
      entities: { banks: entities = {} } = {},
      result: ids = [],
    } = normalize(response.data, banksSchema);
    return dispatch({
      type: FETCH_BANKS_SUCCESS,
      payload: {
        entities,
        ids,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_BANKS_FAILURE,
      payload: {},
    });
  }
};

const getRatesGraphIsFetching = makeGetConvertorRatesGraphIsFetching();
const getRatesGraphIsInitialized = makeGetConvertorRatesGraphIsInitialized();

const getIntervalByPeriod = period => {
  if (period === '6' || period === '29') {
    return 'days';
  }

  if (period === '364' || period === '1826') {
    return 'weeks';
  }

  return 'years';
};

const shouldFetchRatesGraph = (state, params) => {
  const isFetching = getRatesGraphIsFetching(state, params);
  const isInitialized = getRatesGraphIsInitialized(state, params);

  return !(isFetching || isInitialized);
};

export const fetchConvertorRatesGraph = params => async (
  dispatch,
  getState
) => {
  const currentState = getState();

  if (!shouldFetchRatesGraph(currentState, params)) {
    return true;
  }

  if (!params.sourceCurrency || !params.targetCurrency) {
    return dispatch({
      type: FETCH_CONVERTOR_RATES_GRAPH_FAILURE,
      payload: { currenciesKey: '', periodKey: params.period },
    });
  }

  const currenciesKey = `${params.sourceCurrency}-${params.targetCurrency}`;

  dispatch({
    type: FETCH_CONVERTOR_RATES_GRAPH_REQUEST,
    payload: { currenciesKey, periodKey: params.period },
  });

  const requestUrl = `${
    process.env.APP_EXCHANGE_RATE_DYNAMICS_API
  }/graph/${params.sourceCurrency.toLowerCase()}/${params.targetCurrency.toLowerCase()}/${
    params.startDate
  }/${params.endDate}/${getIntervalByPeriod(params.period)}/`;

  try {
    const response = await request({
      url: requestUrl,
    });

    const {
      entities: { ratesGraph: entities = {} } = {},
      result: ids = [],
    } = normalize(response.data, schema);

    return dispatch({
      type: FETCH_CONVERTOR_RATES_GRAPH_SUCCESS,
      payload: {
        currenciesKey: `${params.sourceCurrency}-${params.targetCurrency}`,
        periodKey: params.period,
        entities,
        ids,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_CONVERTOR_RATES_GRAPH_FAILURE,
      payload: { currenciesKey, periodKey: params.period },
    });
  }
};

const getRequestCurrencyType = type => {
  return type ? `&type=${type}` : '';
};

const shouldFetchCurrencies = state => {
  return !getCurrenciesFetching(state);
};

export const fetchCurrencies = params => async (dispatch, getState) => {
  const currentState = getState();

  if (!shouldFetchCurrencies(currentState)) {
    return true;
  }

  dispatch({
    type: FETCH_CURRENCIES_REQUEST,
    payload: {},
  });

  const items = getCurrenciesItems(currentState)[params.type || 'all'];
  const locale = params.locale || params.intl.locale;

  const requestUrl = `${
    process.env.APP_CURRENCIES_API_URL
  }/list/?locale=${getLocaleInDBFormat(locale)}${getRequestCurrencyType(
    params.type
  )}&start=${items.length}&cpp=${15}`;

  try {
    const response = await request({
      url: requestUrl,
    });

    const rawList = filterExcludedCurrencies(response?.list || []);

    const { entities: { currencies = {} } = {} } = normalize(
      rawList,
      currenciesSchema
    );

    const list = Object.values(currencies);

    return dispatch({
      type: FETCH_CURRENCIES_SUCCESS,
      payload: { list, type: params.type || 'all' },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_CURRENCIES_FAILURE,
      payload: {},
    });
  }
};

const shouldFetchLinksData = (state, props) => {
  const isFetching = getIsLinksDataFetching(state, props);
  const isInitialized = getIsLinksDataInitialized(state, props);

  return !(isInitialized || isFetching);
};

export const fetchLinksData = (params = {}) => async (dispatch, getState) => {
  const locale = params.locale || params.intl.locale;
  const { location } = params;

  if (!location || !locale) {
    return false;
  }
  const url = getURIByLocationWithParams(location, locale);
  const currentState = getState();

  if (!shouldFetchLinksData(currentState, { location, locale })) {
    return true;
  }

  dispatch({
    type: FETCH_LINKS_DATA_REQUEST,
    payload: {},
  });

  const requestURL = `${process.env.APP_SEO_LINKS_BLOCKS_API_ROOT}?url=${process.env.APP_ROUTER_BASENAME}${location.pathname}&locale=${locale}`;

  try {
    const { data } = await request({
      url: requestURL,
    });

    const filteredData = filterExcludedLinks(data);

    const normalizedData =
      filteredData && Array.isArray(filteredData) && filteredData.length > 0
        ? normalize(filteredData, linksSchema)
        : { entities: {}, result: [] };

    const { entities, result: ids } = normalizedData;

    return dispatch({
      type: FETCH_LINKS_DATA_SUCCESS,
      payload: {
        url,
        entities: entities || {},
        ids: ids || [],
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_LINKS_DATA_FAILURE,
      payload: {},
    });
  }
};
